import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Copy, CheckCircle2, Ban, ShieldAlert, Loader2 } from 'lucide-react';
import { ReconciliationIssueRecord } from '@/types/database';
import { reconciliationService } from '@/services/reconciliationService';

interface DuplicateModalProps {
  issue: ReconciliationIssueRecord | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const DuplicateModal: React.FC<DuplicateModalProps> = ({
  issue,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [decision, setDecision] = useState<'keep_both' | 'soft_void' | 'unresolved'>('keep_both');
  const [voidReason, setVoidReason] = useState<string>('Confirmed duplicate transaction in Excel ledger import');
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const sourceRows: number[] = Array.isArray(issue?.source_rows)
    ? issue!.source_rows
    : typeof issue?.source_rows === 'string'
    ? JSON.parse(issue!.source_rows || '[]')
    : [];

  const handleSubmit = async () => {
    if (!issue) return;
    setError(null);
    setSubmitting(true);

    try {
      await reconciliationService.resolveDuplicate(
        issue.id,
        {
          decision,
          void_table: 'expenses', // standard duplicate candidate table
          void_record_id: issue.entity_id || undefined,
          void_reason: voidReason,
          notes: adminNotes,
        },
        adminNotes
      );

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error resolving duplicate issue:', err);
      setError(err.message || 'Failed to save duplicate resolution');
    } finally {
      setSubmitting(false);
    }
  };

  if (!issue) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-amber-50 text-amber-700 border border-amber-200">
              <Copy className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-slate-900">
                Candidate Duplicate Review
              </DialogTitle>
              <p className="text-xs text-slate-500 mt-0.5">
                Non-destructive duplicate resolution (Never hard-deletes; uses soft-voiding audit trail).
              </p>
            </div>
          </div>
        </DialogHeader>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Duplicate Ground Truth */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3.5 space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">
              Candidate Duplicate Evidence
            </span>
            <div className="flex items-center gap-1.5">
              <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-slate-200 text-slate-700 font-bold">
                {issue.source_sheet}
              </span>
              {sourceRows.length > 0 && (
                <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-indigo-100 text-indigo-800 font-bold">
                  Rows: {sourceRows.join(', ')}
                </span>
              )}
            </div>
          </div>
          <div>
            <span className="text-slate-500">Transaction Fact Summary:</span>
            <div className="font-mono font-bold text-slate-900 mt-0.5 text-xs">{issue.legacy_value}</div>
          </div>
          <div>
            <span className="text-slate-500">Diagnostic Reason:</span>
            <div className="text-slate-700 mt-0.5">{issue.description}</div>
          </div>
        </div>

        {/* Decision Options */}
        <div className="space-y-3">
          <Label className="text-xs font-bold text-slate-800 uppercase tracking-wider">
            Resolution Action:
          </Label>

          {/* Option 1: Keep Both */}
          <div
            onClick={() => setDecision('keep_both')}
            className={`p-3.5 rounded-lg border cursor-pointer transition-all ${
              decision === 'keep_both'
                ? 'border-emerald-600 bg-emerald-50/60 ring-1 ring-emerald-600'
                : 'border-slate-200 hover:border-slate-300 bg-white'
            }`}
          >
            <div className="flex items-start gap-2.5">
              <input
                type="radio"
                name="duplicate_decision"
                checked={decision === 'keep_both'}
                onChange={() => setDecision('keep_both')}
                className="mt-0.5 text-emerald-600 focus:ring-emerald-500"
              />
              <div>
                <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  Keep Both — Legitimate Repeated Transactions
                </span>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Transactions occurred on the same date/cycle independently (e.g. multiple feed deliveries or medicine treatments).
                </p>
              </div>
            </div>
          </div>

          {/* Option 2: Confirm Duplicate (Soft Void) */}
          <div
            onClick={() => setDecision('soft_void')}
            className={`p-3.5 rounded-lg border cursor-pointer transition-all ${
              decision === 'soft_void'
                ? 'border-amber-600 bg-amber-50/60 ring-1 ring-amber-600'
                : 'border-slate-200 hover:border-slate-300 bg-white'
            }`}
          >
            <div className="flex items-start gap-2.5">
              <input
                type="radio"
                name="duplicate_decision"
                checked={decision === 'soft_void'}
                onChange={() => setDecision('soft_void')}
                className="mt-0.5 text-amber-600 focus:ring-amber-500"
              />
              <div className="space-y-2 flex-1">
                <div>
                  <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <ShieldAlert className="w-3.5 h-3.5 text-amber-600" />
                    Confirm Duplicate — Soft-Void Duplicate Record with Reason
                  </span>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Marks the duplicate line with <code className="font-mono">is_void = true</code>. Preserves raw row lineage and audit columns without deleting.
                  </p>
                </div>

                {decision === 'soft_void' && (
                  <div className="pt-2 border-t border-amber-100">
                    <Label className="text-[11px] font-semibold text-slate-700">Mandatory Soft-Void Reason *</Label>
                    <Input
                      type="text"
                      value={voidReason}
                      onChange={(e) => setVoidReason(e.target.value)}
                      placeholder="e.g. Accidental double entry in historical ledger"
                      className="mt-1 text-xs h-8"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Audit Notes */}
        <div>
          <Label className="text-xs font-semibold text-slate-700">Resolution Audit Notes</Label>
          <Input
            type="text"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder="e.g. Cross-checked with bodega receipt log"
            className="mt-1 text-xs"
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-0 pt-2 border-t border-slate-100">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            className="min-h-[44px] text-xs font-semibold text-slate-700"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="min-h-[44px] bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5 text-xs font-semibold"
          >
            {submitting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                <span>Confirm Duplicate Decision</span>
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
